import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Chip, Divider, Typography } from '@mui/material'

import { useUAInsightsActions } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { ObjectId, TagRef } from '../../types'
import { SectionCard } from '../SectionCard/SectionCard'

interface CreativeAdTagDimensionListProps {
  tags: TagRef[]
}

/** Full tag list of a creative, one row per dimension. */
export const CreativeAdTagDimensionList: FC<CreativeAdTagDimensionListProps> = ({ tags }) => {
  const { t } = useTranslation('ua-insights')
  const { openTag } = useUAInsightsActions()
  const { tagLabel, dimensionLabel } = useUATaxonomy()

  const tagsByDimension = useMemo(() => {
    const dimensionMap = new Map<ObjectId, TagRef[]>()
    for (const tag of tags) {
      const list = dimensionMap.get(tag.dimensionId) ?? []
      list.push(tag)
      dimensionMap.set(tag.dimensionId, list)
    }

    return Array.from(dimensionMap.entries())
      .map(([dimensionId, values]) => ({
        dimensionId,
        values: [...values].sort((a, b) => tagLabel(a).localeCompare(tagLabel(b))),
      }))
      .sort((a, b) => dimensionLabel(a.dimensionId).localeCompare(dimensionLabel(b.dimensionId)))
  }, [tags, tagLabel, dimensionLabel])

  return (
    <SectionCard title={t('full_tag_list')} disableBodyPadding>
      {tagsByDimension.map(({ dimensionId, values }, index) => (
        <Box key={dimensionId}>
          {index > 0 && <Divider sx={{ mx: 2 }} />}
          <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', minWidth: 120, flexShrink: 0 }}>
              {dimensionLabel(dimensionId)}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {values.map((v) => (
                <Chip key={v.id} label={tagLabel(v)} size="small" variant="outlined" onClick={() => openTag(v.id)} clickable />
              ))}
            </Box>
          </Box>
        </Box>
      ))}
    </SectionCard>
  )
}
